import React, { useState, useEffect } from 'react';

const SYSTEM_FIELDS = [
  { key: 'patient_first_name', label: 'Patient First Name' },
  { key: 'patient_last_name',  label: 'Patient Last Name' },
  { key: 'email_address',      label: 'Email Address' },
  { key: 'phone_number',       label: 'Phone Number' },
  { key: 'clinic_id',          label: 'Clinic ID' },
  { key: 'location_id',        label: 'Location ID' },
  { key: 'profile_id',         label: 'Profile ID' },
];

const EMPTY_PARSER = Object.fromEntries(SYSTEM_FIELDS.map(f => [f.key, '']));

const EMPTY_SOURCE = {
  location_id_type: 'external',
  data_path: '',
  trigger_value: 'APPOINTMENT-COMPLETE',
  parser: { ...EMPTY_PARSER },
};

async function fetchSources(token, companyId) {
  return fetch(process.env.REACT_APP_API_URL + '/app/company/' + companyId + '/sources', {
    method: 'GET',
    headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
  }).then(r => r.json()).catch(e => ({ error: e.message }));
}

async function saveSources(token, companyId, sources) {
  return fetch(process.env.REACT_APP_API_URL + '/app/company/' + companyId + '/sources', {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sources }),
  }).then(r => r.json()).catch(e => ({ error: e.message }));
}

export default function CompanySources({ token, companyId }) {
  const [sources, setSources] = useState({});
  const [newName, setNewName] = useState('');
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchSources(token, companyId).then(data => {
      if (!data.error) {
        setSources(data.sources || {});
        setLoaded(true);
      }
    });
  }, [token, companyId]);

  const handleParserField = (sourceName, field, value) => {
    setSources(prev => ({
      ...prev,
      [sourceName]: {
        ...prev[sourceName],
        parser: { ...prev[sourceName].parser, [field]: value },
      },
    }));
  };

  const handleMeta = (sourceName, field, value) => {
    setSources(prev => ({
      ...prev,
      [sourceName]: { ...prev[sourceName], [field]: value },
    }));
  };

  const handleAdd = () => {
    const name = newName.trim().toUpperCase();
    if (!name || sources[name]) return;
    setSources(prev => ({ ...prev, [name]: { ...EMPTY_SOURCE, parser: { ...EMPTY_PARSER } } }));
    setNewName('');
  };

  const handleRemove = (name) => {
    setSources(prev => { const next = { ...prev }; delete next[name]; return next; });
  };

  const handleSave = async () => {
    setError(false); setSuccess(false);
    const data = await saveSources(token, companyId, sources);
    if (data.error) { setError(true); return; }
    setSuccess(true);
  };

  if (!loaded) return null;

  return (
    <div className="uk-card uk-card-default uk-margin-top">
      <div className="uk-card-header">
        <h3 className="uk-card-title">ORM Sources</h3>
      </div>
      <div className="uk-card-body">
        {error && <div className="uk-alert-danger" uk-alert=""><p>Error saving sources.</p></div>}
        {success && <div className="uk-alert-success" uk-alert=""><a className="uk-alert-close" uk-close=""></a><p>Sources saved.</p></div>}

        {Object.keys(sources).length === 0 && (
          <p className="uk-text-muted">No source types configured.</p>
        )}

        {Object.entries(sources).map(([name, cfg]) => (
          <div key={name} className="uk-card uk-card-small uk-card-body uk-margin-small-bottom" style={{ background: '#f8f8f8' }}>
            <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom">
              <strong>{name}</strong>
              <button className="uk-button uk-button-danger uk-button-small" onClick={() => handleRemove(name)}>Remove</button>
            </div>

            <div className="uk-margin-small-bottom uk-flex uk-flex-middle" uk-grid="">
              <div>
                <label className="uk-form-label">Location ID type</label>
                <select
                  className="uk-select uk-form-small uk-width-auto"
                  value={cfg.location_id_type || 'external'}
                  onChange={e => handleMeta(name, 'location_id_type', e.target.value)}
                >
                  <option value="external">External ID (name lookup)</option>
                  <option value="db_id">DB ID (direct)</option>
                </select>
              </div>
              <div>
                <label className="uk-form-label">Data path <span className="uk-text-muted uk-text-small">(nested key where record data lives, e.g. customData)</span></label>
                <input
                  className="uk-input uk-form-small"
                  placeholder="e.g. customData"
                  value={cfg.data_path || ''}
                  onChange={e => handleMeta(name, 'data_path', e.target.value)}
                />
              </div>
              <div>
                <label className="uk-form-label">Trigger value <span className="uk-text-muted uk-text-small">(default: APPOINTMENT-COMPLETE)</span></label>
                <input
                  className="uk-input uk-form-small"
                  placeholder="e.g. COMPLETE"
                  value={cfg.trigger_value || ''}
                  onChange={e => handleMeta(name, 'trigger_value', e.target.value)}
                />
              </div>
            </div>

            <table className="uk-table uk-table-small uk-table-divider uk-table-striped">
              <thead>
                <tr>
                  <th>System Field</th>
                  <th>Incoming Payload Field</th>
                </tr>
              </thead>
              <tbody>
                {SYSTEM_FIELDS.map(f => (
                  <tr key={f.key}>
                    <td><code>{f.key}</code><br/><span className="uk-text-muted uk-text-small">{f.label}</span></td>
                    <td>
                      <input
                        className="uk-input uk-form-small"
                        placeholder="payload field name"
                        value={(cfg.parser || {})[f.key] || ''}
                        onChange={e => handleParserField(name, f.key, e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        <div className="uk-flex uk-flex-middle uk-margin-top" uk-grid="">
          <div className="uk-width-expand">
            <input
              className="uk-input"
              placeholder="Source name (e.g. GHL)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
          </div>
          <div className="uk-width-auto">
            <button className="uk-button uk-button-default" onClick={handleAdd}>Add Source</button>
          </div>
        </div>

        <div className="uk-margin-top">
          <button className="uk-button uk-button-primary" onClick={handleSave}>Save Sources</button>
        </div>
      </div>
    </div>
  );
}
